if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mobileMql = window.matchMedia('(max-width: 749px)');
        this.orderColorMedia();
        this.positionInitialActiveMedia();
        requestAnimationFrame(() => this.positionInitialActiveMedia());
        this.initializeDeferredMedia();
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      orderColorMedia() {
        if (Number(this.dataset.kokoStyleOptionPosition) > 0) return;
        const list = this.querySelector('.product__media-list');
        if (!list) return;
        const items = Array.from(list.querySelectorAll('li[data-media-id]'));
        const colors = new Set(items.map(item => item.dataset.kokoMediaColor).filter(Boolean));
        for (const color of colors) {
          const group = items.filter(item => item.dataset.kokoMediaColor === color);
          const photo = group.find(item => item.querySelector('img')
            && !item.querySelector('.media-type-video, .media-type-external_video, .media-type-model'));
          if (!photo) continue;
          let previous = photo;
          for (const video of group.filter(item => item.querySelector('.media-type-video, .media-type-external_video'))) {
            if (previous.nextElementSibling !== video) previous.after(video);
            previous = video;
          }
        }
        this.elements.viewer.resetPages?.();
      }

      positionInitialActiveMedia() {
        const slider = this.elements.viewer?.slider;
        const activeMedia = slider?.querySelector('.product__media-item.is-active');
        if (!slider || !activeMedia) return;
        this.showMediaPair(activeMedia);
      }

      initializeDeferredMedia() {
        const slider = this.elements.viewer?.slider;
        if (!slider) return;
        const hydrateInteractionMedia = (event) => {
          const item = event.target.closest?.('[data-media-id]') || slider.querySelector('.product__media-item.is-active');
          this.hydrateMediaNeighborhood(item);
        };
        slider.addEventListener('pointerdown', hydrateInteractionMedia, { passive: true });
        slider.addEventListener('touchstart', hydrateInteractionMedia, { passive: true });
        slider.addEventListener('focusin', hydrateInteractionMedia);
        this.deferredMediaMutationObserver = new MutationObserver(() => this.observeDeferredMedia());
        this.deferredMediaMutationObserver.observe(slider, { childList: true, subtree: true });
        this.mobileMql.addEventListener('change', (event) => {
          if (event.matches) this.observeDeferredMedia();
        });
        this.observeDeferredMedia();
      }

      hydrateDeferredMedia(item) {
        const source = item?.querySelector?.('[data-koko-product-gallery-mobile-srcset]');
        if (!source) return;
        const srcset = source.dataset.kokoProductGalleryMobileSrcset;
        if (srcset) source.srcset = srcset;
        delete source.dataset.kokoProductGalleryMobileSrcset;
        item.dataset.kokoProductGalleryMobileLoaded = 'true';
        this.deferredMediaObserver?.unobserve(item);
      }

      hydrateMediaNeighborhood(item) {
        if (!item) return;
        this.hydrateDeferredMedia(item.previousElementSibling);
        this.hydrateDeferredMedia(item);
        this.hydrateDeferredMedia(item.nextElementSibling);
      }

      promoteDesktopMedia(item, fetchPriority) {
        if (!this.mql.matches) return;
        const image = item?.querySelector?.('img');
        if (!image) return;
        image.loading = 'eager';
        image.fetchPriority = fetchPriority;
      }

      showMediaPair(item) {
        const slider = this.elements.viewer?.slider;
        if (!slider || !item) return;
        this.hydrateMediaNeighborhood(item);
        this.upgradeMobileMediaQuality(item);
        this.promoteDesktopMedia(item, 'high');
        this.promoteDesktopMedia(item.nextElementSibling, 'auto');
        if (this.instantPositionFrame) cancelAnimationFrame(this.instantPositionFrame);
        slider.classList.add('koko-gallery--instant-position');
        slider.scrollLeft = item.offsetLeft;
        this.instantPositionFrame = requestAnimationFrame(() => {
          slider.classList.remove('koko-gallery--instant-position');
        });
      }

      upgradeMobileMediaQuality(item) {
        const source = item?.querySelector?.('[data-koko-product-gallery-mobile-hq-srcset]');
        if (!source) return;
        const srcset = source.dataset.kokoProductGalleryMobileHqSrcset;
        if (srcset) source.srcset = srcset;
        delete source.dataset.kokoProductGalleryMobileHqSrcset;
      }

      observeDeferredMedia() {
        if (!this.mobileMql.matches) return;
        const slider = this.elements.viewer?.slider;
        if (!slider) return;
        const items = Array.from(slider.querySelectorAll('[data-koko-product-gallery-mobile-srcset]'))
          .map((source) => source.closest('.product__media-item'))
          .filter(Boolean);
        if (typeof IntersectionObserver === 'undefined') {
          items.forEach((item) => this.hydrateDeferredMedia(item));
          return;
        }
        if (!this.deferredMediaObserver) {
          this.deferredMediaObserver = new IntersectionObserver(
            (entries) => entries.forEach((entry) => {
              if (entry.isIntersecting) this.hydrateDeferredMedia(entry.target);
            }),
            { root: this.elements.viewer.slider, rootMargin: '0px 300px' }
          );
        }
        items.forEach((item) => this.deferredMediaObserver.observe(item));
      }

      onSlideChanged(event) {
        this.hydrateMediaNeighborhood(event.detail.currentElement);
        this.upgradeMobileMediaQuality(event.detail.currentElement);
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.hydrateMediaNeighborhood(activeMedia);
        this.upgradeMobileMediaQuality(activeMedia);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.showMediaPair(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          const activeMediaRect = activeMedia.getBoundingClientRect();
          const headerBottom = this.mobileMql.matches
            ? Math.max(0, document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0)
            : 0;
          // Don't scroll if the image is already clear of the sticky header
          if (activeMediaRect.top >= headerBottom - 0.5) return;
          const top = activeMediaRect.top + window.scrollY - headerBottom;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
